import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Rating,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  Pencil,
  Send,
  CornerUpRight,
  UserPlus,
  CheckCircle2,
  Timer,
  Paperclip,
  Upload,
  Banknote,
  RotateCw,
  MessageSquare,
  StickyNote,
  History,
  ShieldCheck,
  Star,
  AlertTriangle,
  Inbox,
} from "lucide-react";

import ComplaintForm from "./ComplaintForm";
import {
  EmptyState,
  Field,
  GlassCard,
  PERMISSION,
  PageShell,
  PersonAvatar,
  PriorityChip,
  SectionTitle,
  SlaChip,
  SlaMeter,
  StatusChip,
  T,
  can,
  formatCurrency,
  formatDateTime,
  formatDuration,
  formatRelative,
  ghostButtonSx,
  primaryButtonSx,
} from "./shared";
import {
  addInternalNote,
  assignComplaint,
  changeStatus,
  errorMessage,
  fetchComplaint,
  fetchCustomerHistory,
  fetchFormMeta,
  forwardComplaint,
  logCommunication,
  modifySla,
  openAttachment,
  processRefund,
  reviewRefund,
  setResolution,
  submitFeedback,
  submitQaScore,
  uploadAttachments,
} from "./api";

const REFUND_STATUS_LABELS = {
  not_applicable: "No payout",
  pending_approval: "Pending Finance approval",
  approved: "Approved",
  processed: "Processed",
  rejected: "Rejected",
};

/**
 * Generic action dialog, so the ten actions on a complaint do not each need
 * their own near-identical dialog component.
 */
const ActionDialog = ({
  open, title, description, onClose, onConfirm, busy, children, confirmLabel = "Confirm",
}) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: T.radiusCard } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: T.ink }}>{title}</Typography>
      {description && (
        <Typography sx={{ fontSize: "0.78rem", color: T.inkSecondary, mt: 0.25 }}>
          {description}
        </Typography>
      )}
    </DialogTitle>
    <DialogContent dividers sx={{ borderColor: T.border }}>
      {children}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", color: T.inkSecondary }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
        sx={primaryButtonSx}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

/** One entry in the status/escalation trail, with its connector line. */
const TimelineEntry = ({ entry, isLast }) => {
  const tint = entry.kind === "escalation" ? "#F59E0B" : T.brandLight;

  return (
    <Box sx={{ display: "flex", gap: 1.5, position: "relative", pb: isLast ? 0 : 2.5 }}>
      {/* rail */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: `${tint}1A`,
            color: tint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {entry.kind === "escalation" ? <CornerUpRight size={13} /> : <History size={13} />}
        </Box>
        {!isLast && <Box sx={{ width: 2, flexGrow: 1, bgcolor: T.border, mt: 0.5, borderRadius: 1 }} />}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink }}>
            {entry.title}
          </Typography>
          {entry.trigger === "sla_breach" && (
            <Chip
              label="SLA breach"
              size="small"
              sx={{
                height: 18,
                fontSize: "0.62rem",
                fontWeight: 700,
                bgcolor: "#FEE2E2",
                color: "#B91C1C",
                borderRadius: "999px",
              }}
            />
          )}
        </Stack>
        {entry.detail && (
          <Typography sx={{ fontSize: "0.76rem", color: T.inkSecondary, mt: 0.25 }}>
            {entry.detail}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted, mt: 0.25 }}>
          {entry.actor || "System"} · {formatRelative(entry.at)}
        </Typography>
      </Box>
    </Box>
  );
};

const ComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const [dialog, setDialog] = useState({ type: null, values: {} });
  const [editOpen, setEditOpen] = useState(false);
  // The internal-note box is inline in the tab, not a dialog, so it keeps its
  // own draft rather than sharing the dialog state.
  const [noteDraft, setNoteDraft] = useState("");

  // Memoised: a fresh array each render would re-fire the customer-history
  // effect below on every render.
  const permissions = useMemo(() => meta?.me?.permissions || [], [meta]);
  const notify = (message, severity = "success") => setToast({ open: true, message, severity });

  const openDialog = (type, values = {}) => setDialog({ type, values });
  const closeDialog = () => setDialog({ type: null, values: {} });
  const setValue = (key, value) =>
    setDialog((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchComplaint(id);
      setComplaint(data);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Failed to load complaint"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFormMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Past complaints from the same customer, so an agent can see at a glance
  // whether this is a repeat problem.
  useEffect(() => {
    if (!complaint || !can(permissions, PERMISSION.CUSTOMER_HISTORY)) return;

    const params = {};
    if (complaint.customer?.customerId) params.customerId = complaint.customer.customerId;
    if (complaint.customer?.mobile) params.mobile = complaint.customer.mobile;
    if (complaint.customer?.email) params.email = complaint.customer.email;
    if (!Object.keys(params).length) return;

    fetchCustomerHistory(params).then(setHistory).catch(() => setHistory(null));
  }, [complaint, permissions]);

  /** Runs an action, then refreshes and reports the outcome consistently. */
  const run = async (action, successFallback) => {
    setBusy(true);
    try {
      const response = await action();
      notify(response?.message || successFallback);
      closeDialog();
      await load();
    } catch (err) {
      notify(errorMessage(err, "Action failed"), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleAttachmentUpload = async (files) => {
    if (!files?.length) return;
    await run(() => uploadAttachments(id, files), "Attachments uploaded");
  };

  if (loading && !complaint) {
    return (
      <PageShell>
        <Skeleton variant="rounded" height={96} sx={{ borderRadius: T.radiusCard, mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rounded" height={220} sx={{ borderRadius: T.radiusCard, mb: 2 }} />
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: T.radiusCard }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: T.radiusCard, mb: 2 }} />
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: T.radiusCard }} />
          </Grid>
        </Grid>
      </PageShell>
    );
  }

  if (error && !complaint) {
    return (
      <PageShell maxWidth="md">
        <GlassCard>
          <EmptyState
            icon={AlertTriangle}
            title="Could not open this complaint"
            description={error}
            action={
              <Button
                startIcon={<ArrowLeft size={16} />}
                onClick={() => navigate("/complaints")}
                variant="outlined"
                sx={ghostButtonSx}
              >
                Back to complaints
              </Button>
            }
          />
        </GlassCard>
      </PageShell>
    );
  }

  const c = complaint;
  const refundPending = c.resolution?.refundStatus === "pending_approval";
  const refundApproved = c.resolution?.refundStatus === "approved";
  const isClosed = ["Resolved", "Closed"].includes(c.status);

  // Merge status changes and escalations into one chronological trail.
  const timeline = [
    ...(c.statusHistory || []).map((entry) => ({
      kind: "status",
      at: entry.changedAt,
      title: entry.from ? `${entry.from} → ${entry.to}` : `Registered as ${entry.to}`,
      detail: entry.reason,
      actor: entry.changedBy?.name,
    })),
    ...(c.escalations || []).map((entry) => ({
      kind: "escalation",
      at: entry.escalatedAt,
      title: `Forwarded to ${
        meta?.forwardQueues?.find((q) => q.key === entry.toQueue)?.label || entry.toQueue
      }`,
      detail: entry.reason,
      actor: entry.escalatedBy?.name,
      trigger: entry.trigger,
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  const actions = [
    { show: can(permissions, PERMISSION.UPDATE), icon: Pencil, label: "Edit", onClick: () => setEditOpen(true) },
    {
      show: can(permissions, PERMISSION.COMMUNICATION_LOG),
      icon: Send, label: "Log Contact",
      onClick: () => openDialog("communication", { channel: "Call", direction: "Outbound" }),
    },
    {
      show: can(permissions, PERMISSION.ASSIGN), icon: UserPlus, label: "Assign",
      onClick: () => openDialog("assign", { memberId: c.assignedTo?.memberId || "" }),
    },
    {
      show: can(permissions, PERMISSION.FORWARD), icon: CornerUpRight, label: "Forward",
      onClick: () => openDialog("forward", {}),
    },
    {
      show: can(permissions, PERMISSION.RESOLVE) && !isClosed, icon: CheckCircle2, label: "Resolve",
      primary: true,
      onClick: () => openDialog("resolve", { resolutionType: "", amount: "", summary: "" }),
    },
    {
      show: can(permissions, PERMISSION.STATUS_CHANGE), icon: History, label: "Change Status",
      onClick: () => openDialog("status", { status: "", reason: "" }),
    },
    {
      show: can(permissions, PERMISSION.SLA_MODIFY), icon: Timer, label: "Modify SLA",
      onClick: () => openDialog("sla", { resolutionDueAt: "", reason: "" }),
    },
    {
      show: refundPending && can(permissions, PERMISSION.REFUND_APPROVE), icon: Banknote,
      label: "Review Refund", primary: true,
      onClick: () => openDialog("refundReview", { decision: "approve", rejectionReason: "" }),
    },
    {
      show: refundApproved && can(permissions, PERMISSION.REFUND_PROCESS), icon: Banknote,
      label: "Mark Refund Paid", primary: true,
      onClick: () => run(() => processRefund(id), "Refund processed"),
    },
    {
      show: isClosed, icon: Star, label: "Record Feedback",
      onClick: () => openDialog("feedback", { rating: 0, nps: "", comment: "" }),
    },
    {
      show: isClosed && can(permissions, PERMISSION.QA_SCORE), icon: ShieldCheck, label: "QA Score",
      onClick: () => openDialog("qa", { score: "", auditNotes: "" }),
    },
  ].filter((action) => action.show);

  return (
    <>
      <PageShell>
            <Breadcrumbs sx={{ mb: 1.5, fontSize: "0.78rem" }}>
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/complaints")}
                sx={{ fontSize: "0.78rem", color: T.inkSecondary }}
              >
                Complaints
              </Link>
              <Typography sx={{ fontSize: "0.78rem", color: T.ink, fontWeight: 600 }}>
                {c.ticketId}
              </Typography>
            </Breadcrumbs>

            {/* ---- Header ---- */}
            <GlassCard sx={{ mb: 2, overflow: "hidden" }}>
              <Box sx={{ p: 2.5 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate("/complaints")}
                    sx={{ border: `1px solid ${T.border}`, borderRadius: T.radiusControl, display: { xs: "none", md: "inline-flex" } }}
                  >
                    <ArrowLeft size={16} color={T.inkSecondary} />
                  </IconButton>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: T.ink }}>
                        {c.ticketId}
                      </Typography>
                      <StatusChip status={c.status} />
                      <PriorityChip priority={c.priority} />
                      <SlaChip slaStatus={c.slaStatus} />
                      {c.reopenCount > 0 && (
                        <Chip
                          label={`Reopened ${c.reopenCount}×`}
                          size="small"
                          sx={{
                            height: 24, fontSize: "0.72rem", fontWeight: 600,
                            bgcolor: "#FEE2E2", color: "#B91C1C", borderRadius: "999px",
                          }}
                        />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: "0.78rem", color: T.inkSecondary, mt: 0.5 }}>
                      {c.category}
                      {c.subCategory ? ` · ${c.subCategory}` : ""} · via {c.source} · registered{" "}
                      {formatDateTime(c.createdAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: { md: 190 } }}>
                    <SlaMeter slaStatus={c.slaStatus} />
                  </Box>

                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={load}>
                      <RotateCw size={16} color={T.inkSecondary} />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {actions.length > 0 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: T.border }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {actions.map((action) => (
                        <Button
                          key={action.label}
                          size="small"
                          variant={action.primary ? "contained" : "outlined"}
                          startIcon={<action.icon size={15} />}
                          onClick={action.onClick}
                          sx={action.primary ? primaryButtonSx : ghostButtonSx}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  </>
                )}
              </Box>
            </GlassCard>

            <Grid container spacing={2}>
              {/* ---- Left column ---- */}
              <Grid item xs={12} md={8}>
                <GlassCard sx={{ p: 2.5, mb: 2 }}>
                  <SectionTitle icon={MessageSquare}>Complaint</SectionTitle>
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      color: T.ink,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      bgcolor: T.surfaceMuted,
                      p: 1.75,
                      borderRadius: T.radiusControl,
                    }}
                  >
                    {c.description}
                  </Typography>

                  <Divider sx={{ my: 2, borderColor: T.border }} />

                  <SectionTitle
                    icon={Paperclip}
                    action={
                      can(permissions, PERMISSION.ATTACHMENT_UPLOAD) && (
                        <Button
                          component="label"
                          size="small"
                          startIcon={<Upload size={14} />}
                          sx={{ textTransform: "none", color: T.brand, fontWeight: 600 }}
                        >
                          Add
                          <input
                            hidden multiple type="file" accept="image/*,video/*,.pdf"
                            onChange={(e) => handleAttachmentUpload(Array.from(e.target.files).slice(0, 5))}
                          />
                        </Button>
                      )
                    }
                  >
                    Attachments ({c.attachments?.length || 0})
                  </SectionTitle>

                  {c.attachments?.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {c.attachments.map((file) => (
                        <Chip
                          key={file._id}
                          icon={<Paperclip size={13} />}
                          label={file.originalName || file.fileName}
                          size="small"
                          clickable
                          onClick={() =>
                            openAttachment(id, file).catch((err) =>
                              notify(errorMessage(err, "Could not open attachment"), "error")
                            )
                          }
                          sx={{
                            borderRadius: T.radiusControl,
                            bgcolor: T.surfaceMuted,
                            border: `1px solid ${T.border}`,
                            "&:hover": { bgcolor: T.brandSoft, borderColor: T.brandLight },
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: "0.78rem", color: T.inkMuted }}>
                      No proof attached
                    </Typography>
                  )}
                </GlassCard>

                {/* ---- Tabs ---- */}
                <GlassCard sx={{ overflow: "hidden" }}>
                  <Tabs
                    value={tab}
                    onChange={(_, value) => setTab(value)}
                    sx={{
                      borderBottom: `1px solid ${T.border}`,
                      px: 1,
                      minHeight: 44,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        minHeight: 44,
                        color: T.inkSecondary,
                      },
                      "& .Mui-selected": { color: `${T.brand} !important` },
                      "& .MuiTabs-indicator": { bgcolor: T.brand, height: 2.5, borderRadius: 2 },
                    }}
                  >
                    <Tab icon={<History size={15} />} iconPosition="start" label={`Timeline (${timeline.length})`} />
                    <Tab icon={<MessageSquare size={15} />} iconPosition="start" label={`Communication (${c.communicationLog?.length || 0})`} />
                    {can(permissions, PERMISSION.NOTE_VIEW) && (
                      <Tab icon={<StickyNote size={15} />} iconPosition="start" label={`Notes (${c.internalNotes?.length || 0})`} />
                    )}
                  </Tabs>

                  <Box sx={{ p: 2.5 }}>
                    {/* Timeline */}
                    {tab === 0 &&
                      (timeline.length ? (
                        <Box>
                          {timeline.map((entry, index) => (
                            <TimelineEntry
                              key={index}
                              entry={entry}
                              isLast={index === timeline.length - 1}
                            />
                          ))}
                        </Box>
                      ) : (
                        <EmptyState dense icon={History} title="Nothing recorded yet" />
                      ))}

                    {/* Communication log */}
                    {tab === 1 &&
                      (c.communicationLog?.length ? (
                        <Stack spacing={1.5}>
                          {[...c.communicationLog].reverse().map((entry) => {
                            const outbound = entry.direction === "Outbound";
                            return (
                              <Box
                                key={entry._id}
                                sx={{
                                  p: 1.75,
                                  borderRadius: T.radiusControl,
                                  bgcolor: outbound ? T.brandSoft : T.surfaceMuted,
                                  border: `1px solid ${outbound ? "#E0E3FB" : T.border}`,
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                  <Chip
                                    label={entry.direction}
                                    size="small"
                                    sx={{
                                      height: 19, fontSize: "0.62rem", fontWeight: 700,
                                      borderRadius: "999px",
                                      bgcolor: outbound ? T.brand : T.inkMuted,
                                      color: "#fff",
                                    }}
                                  />
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.ink }}>
                                    {entry.channel}
                                  </Typography>
                                  <Box sx={{ flexGrow: 1 }} />
                                  <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted }}>
                                    {formatRelative(entry.contactedAt)}
                                  </Typography>
                                </Stack>
                                {entry.subject && (
                                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink }}>
                                    {entry.subject}
                                  </Typography>
                                )}
                                <Typography sx={{ fontSize: "0.82rem", color: T.ink, whiteSpace: "pre-wrap" }}>
                                  {entry.message}
                                </Typography>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                                  <PersonAvatar name={entry.loggedBy?.name} size={20} />
                                  <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted }}>
                                    {entry.loggedBy?.name || "—"}
                                  </Typography>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <EmptyState
                          dense
                          icon={MessageSquare}
                          title="No contact logged yet"
                          description="Logging the first outbound contact stops the first-response SLA clock."
                        />
                      ))}

                    {/* Internal notes */}
                    {tab === 2 && can(permissions, PERMISSION.NOTE_VIEW) && (
                      <>
                        {can(permissions, PERMISSION.NOTE_ADD) && (
                          <Box sx={{ mb: 2.5 }}>
                            <TextField
                              fullWidth size="small" multiline minRows={2}
                              placeholder="Add an internal note — never shown to the customer"
                              value={noteDraft}
                              onChange={(e) => setNoteDraft(e.target.value)}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusControl, bgcolor: T.surfaceMuted } }}
                            />
                            <Button
                              size="small" variant="contained"
                              disabled={busy || !noteDraft.trim()}
                              onClick={async () => {
                                await run(() => addInternalNote(id, noteDraft), "Note added");
                                setNoteDraft("");
                              }}
                              sx={{ ...primaryButtonSx, mt: 1 }}
                            >
                              Add Note
                            </Button>
                          </Box>
                        )}

                        {c.internalNotes?.length ? (
                          <Stack spacing={1.5}>
                            {[...c.internalNotes].reverse().map((note) => (
                              <Box
                                key={note._id}
                                sx={{
                                  p: 1.75,
                                  borderRadius: T.radiusControl,
                                  bgcolor: "#FFFBEB",
                                  border: "1px solid #FDE9B5",
                                }}
                              >
                                <Typography sx={{ fontSize: "0.82rem", color: T.ink, whiteSpace: "pre-wrap" }}>
                                  {note.note}
                                </Typography>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                                  <PersonAvatar name={note.author?.name} size={20} />
                                  <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted }}>
                                    {note.author?.name || "—"} · {formatRelative(note.createdAt)}
                                  </Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <EmptyState dense icon={StickyNote} title="No internal notes" />
                        )}
                      </>
                    )}
                  </Box>
                </GlassCard>
              </Grid>

              {/* ---- Right column ---- */}
              <Grid item xs={12} md={4}>
                <GlassCard sx={{ p: 2.5, mb: 2 }}>
                  <SectionTitle>Customer</SectionTitle>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <PersonAvatar name={c.customer?.name || c.customer?.mobile} size={40} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: T.ink }} noWrap>
                        {c.customer?.name || "Unnamed customer"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: T.inkSecondary }} noWrap>
                        {c.customer?.mobile || c.customer?.email || "—"}
                      </Typography>
                    </Box>
                  </Stack>
                  <Field label="Customer ID" value={c.customer?.customerId} />
                  <Field label="Email" value={c.customer?.email} />

                  {history && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: T.radiusControl,
                        bgcolor: history.total > 1 ? "#FFFBEB" : T.surfaceMuted,
                        border: `1px solid ${history.total > 1 ? "#FDE9B5" : T.border}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        {history.total > 1 ? (
                          <AlertTriangle size={14} color="#B45309" />
                        ) : (
                          <Inbox size={14} color={T.inkSecondary} />
                        )}
                        <Typography sx={{ fontSize: "0.75rem", color: T.ink }}>
                          <strong>{history.total}</strong> complaint{history.total === 1 ? "" : "s"} from this customer
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </GlassCard>

                <GlassCard sx={{ p: 2.5, mb: 2 }}>
                  <SectionTitle>Order</SectionTitle>
                  <Field label="Order ID" value={c.order?.orderId} />
                  <Field label="Restaurant" value={c.order?.restaurantName} />
                  <Field
                    label="Delivery Partner"
                    value={c.order?.deliveryPartnerName || c.order?.deliveryPartnerId}
                  />
                  <Field label="Order Date" value={formatDateTime(c.order?.orderDateTime)} />
                  <Field
                    label="Amount"
                    value={c.order?.orderAmount ? formatCurrency(c.order.orderAmount) : null}
                  />
                  <Field label="Payment Mode" value={c.order?.paymentMode} />
                  <Field
                    label="Location"
                    value={[c.location?.city, c.location?.region, c.location?.pincode]
                      .filter(Boolean)
                      .join(", ")}
                    sx={{ mb: 0 }}
                  />
                </GlassCard>

                <GlassCard sx={{ p: 2.5, mb: 2 }}>
                  <SectionTitle icon={Timer}>Assignment &amp; SLA</SectionTitle>

                  {c.assignedTo?.name ? (
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                      <PersonAvatar name={c.assignedTo.name} size={32} />
                      <Box>
                        <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: T.ink }}>
                          {c.assignedTo.name}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted }}>
                          {meta?.roleLabels?.[c.assignedTo.role] || c.assignedTo.role}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Chip
                      label="Unassigned"
                      size="small"
                      sx={{ mb: 1.5, borderRadius: "999px", bgcolor: T.surfaceMuted, color: T.inkMuted }}
                    />
                  )}

                  <Field
                    label="Queue"
                    value={meta?.forwardQueues?.find((q) => q.key === c.assignedQueue)?.label || c.assignedQueue}
                  />
                  <Field label="Department" value={meta?.departmentLabels?.[c.department] || c.department} />

                  <Divider sx={{ my: 1.5, borderColor: T.border }} />

                  <Field label="First response due" value={formatDateTime(c.sla?.firstResponseDueAt)} />
                  <Field
                    label="First response at"
                    value={
                      c.sla?.firstRespondedAt
                        ? `${formatDateTime(c.sla.firstRespondedAt)}${c.sla.firstResponseBreached ? " · breached" : ""}`
                        : "Awaiting first response"
                    }
                  />
                  <Field label="Resolution due" value={formatDateTime(c.sla?.resolutionDueAt)} />
                  <Field
                    label="Resolved at"
                    value={
                      c.sla?.resolvedAt
                        ? `${formatDateTime(c.sla.resolvedAt)}${c.sla.resolutionBreached ? " · breached" : ""}`
                        : null
                    }
                    sx={{ mb: c.sla?.overridden ? 1.5 : 0 }}
                  />

                  {c.sla?.overridden && (
                    <Alert
                      severity="info"
                      sx={{ borderRadius: T.radiusControl, py: 0, "& .MuiAlert-message": { fontSize: "0.72rem" } }}
                    >
                      SLA overridden by {c.sla.overriddenBy?.name}: {c.sla.overrideReason}
                    </Alert>
                  )}
                </GlassCard>

                {c.resolution?.type && (
                  <GlassCard sx={{ p: 2.5, mb: 2 }}>
                    <SectionTitle icon={CheckCircle2}>Resolution</SectionTitle>
                    <Field label="Type" value={c.resolution.type} />
                    {c.resolution.amount > 0 && (
                      <>
                        <Field label="Amount" value={formatCurrency(c.resolution.amount)} />
                        <Field
                          label="Refund Status"
                          value={REFUND_STATUS_LABELS[c.resolution.refundStatus]}
                        />
                        {c.resolution.approvedBy?.name && (
                          <Field
                            label="Approved by"
                            value={`${c.resolution.approvedBy.name} · ${formatDateTime(c.resolution.approvedAt)}`}
                          />
                        )}
                        {c.resolution.processedBy?.name && (
                          <Field
                            label="Paid by"
                            value={`${c.resolution.processedBy.name} · ${formatDateTime(c.resolution.processedAt)}`}
                          />
                        )}
                        {c.resolution.rejectionReason && (
                          <Field label="Rejected because" value={c.resolution.rejectionReason} />
                        )}
                      </>
                    )}
                    <Field label="Summary" value={c.resolution.summary} />
                    <Field
                      label="Resolved by"
                      value={
                        c.resolution.resolvedBy?.name
                          ? `${c.resolution.resolvedBy.name} · ${formatDateTime(c.resolution.resolvedAt)}`
                          : null
                      }
                    />
                    {c.sla?.resolvedAt && (
                      <Field
                        label="Time to resolve"
                        value={formatDuration((new Date(c.sla.resolvedAt) - new Date(c.createdAt)) / 60000)}
                        sx={{ mb: 0 }}
                      />
                    )}
                  </GlassCard>
                )}

                {(c.csat?.submittedAt || c.qa?.auditedAt) && (
                  <GlassCard sx={{ p: 2.5 }}>
                    <SectionTitle icon={Star}>Feedback &amp; QA</SectionTitle>
                    {c.csat?.submittedAt && (
                      <>
                        {c.csat.rating && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: "0.68rem", color: T.inkMuted, mb: 0.25 }}>
                              CSAT
                            </Typography>
                            <Rating value={c.csat.rating} readOnly size="small" />
                          </Box>
                        )}
                        <Field label="NPS" value={c.csat.nps} />
                        <Field label="Customer comment" value={c.csat.comment} />
                      </>
                    )}
                    {c.qa?.auditedAt && (
                      <>
                        <Divider sx={{ my: 1.5, borderColor: T.border }} />
                        <Field label="QA Score" value={`${c.qa.score}/100`} />
                        <Field label="Audit notes" value={c.qa.auditNotes} />
                        <Field
                          label="Audited by"
                          value={`${c.qa.auditedBy?.name} · ${formatDateTime(c.qa.auditedAt)}`}
                          sx={{ mb: 0 }}
                        />
                      </>
                    )}
                  </GlassCard>
                )}
              </Grid>
            </Grid>
      </PageShell>

      {/* ---- Action dialogs ---- */}
      <ActionDialog
        open={dialog.type === "status"} title="Change Status" busy={busy}
        description="A reason is required when reopening a complaint"
        onClose={closeDialog}
        onConfirm={() => run(() => changeStatus(id, dialog.values), "Status updated")}
      >
        <TextField
          select fullWidth size="small" label="New status" sx={{ mb: 2 }}
          value={dialog.values.status || ""} onChange={(e) => setValue("status", e.target.value)}
        >
          {(meta?.statuses || []).filter((s) => s !== c.status).map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth size="small" multiline minRows={2} label="Reason"
          value={dialog.values.reason || ""} onChange={(e) => setValue("reason", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "assign"} title="Assign Complaint" busy={busy}
        onClose={closeDialog}
        onConfirm={() => run(() => assignComplaint(id, dialog.values.memberId), "Assignment updated")}
      >
        <TextField
          select fullWidth size="small" label="Assign to"
          value={dialog.values.memberId || ""} onChange={(e) => setValue("memberId", e.target.value)}
        >
          <MenuItem value="">Unassign</MenuItem>
          {(meta?.assignees || []).map((person) => (
            <MenuItem key={person.memberId} value={person.memberId}>
              {person.name} — {person.roleLabel}
            </MenuItem>
          ))}
        </TextField>
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "forward"} title="Forward Complaint" busy={busy}
        description="The complaint moves to Escalated and lands in the chosen queue"
        onClose={closeDialog}
        onConfirm={() => run(() => forwardComplaint(id, dialog.values), "Complaint forwarded")}
      >
        <TextField
          select fullWidth size="small" label="Forward to" sx={{ mb: 2 }}
          value={dialog.values.toQueue || ""} onChange={(e) => setValue("toQueue", e.target.value)}
        >
          {(meta?.forwardQueues || []).map((queue) => (
            <MenuItem key={queue.key} value={queue.key}>
              {queue.label} — {queue.hint}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select fullWidth size="small" label="Assign to a person (optional)" sx={{ mb: 2 }}
          value={dialog.values.memberId || ""} onChange={(e) => setValue("memberId", e.target.value)}
        >
          <MenuItem value="">Leave in the queue</MenuItem>
          {(meta?.assignees || []).map((person) => (
            <MenuItem key={person.memberId} value={person.memberId}>
              {person.name} — {person.roleLabel}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required fullWidth size="small" multiline minRows={2} label="Reason for forwarding"
          value={dialog.values.reason || ""} onChange={(e) => setValue("reason", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "resolve"} title="Resolve Complaint" busy={busy}
        description={
          meta?.me?.refundLimit === null
            ? "You can approve any refund amount"
            : `Refunds above ${formatCurrency(meta?.me?.refundLimit)} go to Finance for approval`
        }
        onClose={closeDialog} confirmLabel="Resolve"
        onConfirm={() =>
          run(
            () =>
              setResolution(id, {
                resolutionType: dialog.values.resolutionType,
                amount: Number(dialog.values.amount) || 0,
                summary: dialog.values.summary,
              }),
            "Resolution recorded"
          )
        }
      >
        <TextField
          select fullWidth size="small" label="Resolution type" sx={{ mb: 2 }}
          value={dialog.values.resolutionType || ""}
          onChange={(e) => setValue("resolutionType", e.target.value)}
        >
          {(meta?.resolutionTypes || []).map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </TextField>
        {["Refund", "Compensation"].includes(dialog.values.resolutionType) && (
          <TextField
            fullWidth size="small" type="number" label="Amount" sx={{ mb: 2 }}
            value={dialog.values.amount || ""} onChange={(e) => setValue("amount", e.target.value)}
          />
        )}
        <TextField
          required fullWidth size="small" multiline minRows={2} label="Resolution summary"
          value={dialog.values.summary || ""} onChange={(e) => setValue("summary", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "communication"} title="Log Customer Contact" busy={busy}
        description="The first outbound entry stops the first-response SLA clock"
        onClose={closeDialog} confirmLabel="Log"
        onConfirm={() => run(() => logCommunication(id, dialog.values), "Communication logged")}
      >
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <TextField
              select fullWidth size="small" label="Channel"
              value={dialog.values.channel || ""} onChange={(e) => setValue("channel", e.target.value)}
            >
              {(meta?.communicationChannels || []).map((ch) => (
                <MenuItem key={ch} value={ch}>{ch}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select fullWidth size="small" label="Direction"
              value={dialog.values.direction || ""} onChange={(e) => setValue("direction", e.target.value)}
            >
              {(meta?.communicationDirections || []).map((dir) => (
                <MenuItem key={dir} value={dir}>{dir}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <TextField
          fullWidth size="small" label="Subject" sx={{ mb: 2 }}
          value={dialog.values.subject || ""} onChange={(e) => setValue("subject", e.target.value)}
        />
        <TextField
          required fullWidth size="small" multiline minRows={3} label="What was said"
          value={dialog.values.message || ""} onChange={(e) => setValue("message", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "sla"} title="Modify SLA" busy={busy}
        description="Overriding the SLA stops it being recalculated automatically"
        onClose={closeDialog}
        onConfirm={() => run(() => modifySla(id, dialog.values), "SLA updated")}
      >
        <TextField
          fullWidth size="small" type="datetime-local" label="New resolution due" sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          value={dialog.values.resolutionDueAt || ""}
          onChange={(e) => setValue("resolutionDueAt", e.target.value)}
        />
        <TextField
          required fullWidth size="small" multiline minRows={2} label="Justification"
          value={dialog.values.reason || ""} onChange={(e) => setValue("reason", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "refundReview"} title="Review Refund" busy={busy}
        description={`${formatCurrency(c.resolution?.amount)} requested`}
        onClose={closeDialog}
        onConfirm={() => run(() => reviewRefund(id, dialog.values), "Refund reviewed")}
      >
        <TextField
          select fullWidth size="small" label="Decision" sx={{ mb: 2 }}
          value={dialog.values.decision || "approve"}
          onChange={(e) => setValue("decision", e.target.value)}
        >
          <MenuItem value="approve">Approve</MenuItem>
          <MenuItem value="reject">Reject</MenuItem>
        </TextField>
        {dialog.values.decision === "reject" && (
          <TextField
            required fullWidth size="small" multiline minRows={2} label="Reason for rejection"
            value={dialog.values.rejectionReason || ""}
            onChange={(e) => setValue("rejectionReason", e.target.value)}
          />
        )}
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "feedback"} title="Record Customer Feedback" busy={busy}
        onClose={closeDialog}
        onConfirm={() =>
          run(
            () =>
              submitFeedback(id, {
                rating: dialog.values.rating || undefined,
                nps: dialog.values.nps === "" ? undefined : Number(dialog.values.nps),
                comment: dialog.values.comment,
              }),
            "Feedback recorded"
          )
        }
      >
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted, mb: 0.5 }}>CSAT rating</Typography>
          <Rating
            value={Number(dialog.values.rating) || 0}
            onChange={(_, value) => setValue("rating", value)}
          />
        </Box>
        <TextField
          fullWidth size="small" type="number" label="NPS (0-10)" sx={{ mb: 2 }}
          inputProps={{ min: 0, max: 10 }}
          value={dialog.values.nps ?? ""} onChange={(e) => setValue("nps", e.target.value)}
        />
        <TextField
          fullWidth size="small" multiline minRows={2} label="Customer comment"
          value={dialog.values.comment || ""} onChange={(e) => setValue("comment", e.target.value)}
        />
      </ActionDialog>

      <ActionDialog
        open={dialog.type === "qa"} title="QA Audit" busy={busy}
        onClose={closeDialog}
        onConfirm={() =>
          run(
            () => submitQaScore(id, { score: Number(dialog.values.score), auditNotes: dialog.values.auditNotes }),
            "QA score recorded"
          )
        }
      >
        <TextField
          required fullWidth size="small" type="number" label="Score (0-100)" sx={{ mb: 2 }}
          inputProps={{ min: 0, max: 100 }}
          value={dialog.values.score ?? ""} onChange={(e) => setValue("score", e.target.value)}
        />
        <TextField
          fullWidth size="small" multiline minRows={3} label="Audit notes"
          value={dialog.values.auditNotes || ""} onChange={(e) => setValue("auditNotes", e.target.value)}
        />
      </ActionDialog>

      <ComplaintForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(message) => { notify(message); load(); }}
        meta={meta}
        complaint={c}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: T.radiusControl, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ComplaintDetail;
